import React, { FC, PropsWithChildren, useMemo } from 'react'
import Box from '@mui/material/Box'
import { DataLoad, PropsInjector } from 'src/components'
import { useSensorListRequest, useSensorStatesRequest, useSensorTypesRequest } from 'src/hooks/api'
import { useQueryParamsState } from 'src/hooks/ui'
import { Sensor } from 'src/api'
import { SensorColumnsDataGrid } from 'src/components/columns/sensor'
import { CommonSensorList } from '../sensor/sensorList/commonSensorsList'

const columns = [
  SensorColumnsDataGrid.Id(),
  SensorColumnsDataGrid.Model,
  SensorColumnsDataGrid.DevEUI,
  SensorColumnsDataGrid.Label,
  SensorColumnsDataGrid.LastPing,
  SensorColumnsDataGrid.SignalStrength,
  SensorColumnsDataGrid.BatteryLevel,
  SensorColumnsDataGrid.DateAdded,
  SensorColumnsDataGrid.State
]

interface ProjectLoRaWanSensorsListProps {
  isLoading?: boolean
  count?: number
  data?: Sensor[]
}

const ProjectLoRaWanSensorsList: FC<ProjectLoRaWanSensorsListProps> = ({ isLoading = false, count = 0, data = [] }) => (
  <Box height="500px">
    <CommonSensorList columns={columns} count={count} data={data} isLoading={isLoading} />
  </Box>
)

interface ProjectLoRaWanSensorsLoaderProps {
  projectId: number
}

const ProjectLoRaWanSensorsLoader: FC<PropsWithChildren<ProjectLoRaWanSensorsLoaderProps>> = ({ projectId, children }) => {
  const params = useQueryParamsState()
  const { data: statesData = [] } = useSensorStatesRequest()
  const { data: typesData } = useSensorTypesRequest()

  const typeId = useMemo(() => typesData?.rows?.find((t) => t.name === 'lorawan_sensor')?.id, [typesData])

  const { isInitialLoading, isError, data, error } = useSensorListRequest({
    params: { ...params, projectId, typeId },
    options: { enabled: typeId !== undefined }
  })

  const rows = useMemo(
    () =>
      (data?.rows || []).map((row) => ({
        ...row,
        state: statesData.find((s) => s.id === row.stateId)
      })),
    [data, statesData]
  )

  const props: ProjectLoRaWanSensorsListProps = {
    isLoading: isInitialLoading,
    count: data?.count || 0,
    data: rows
  }

  return (
    <DataLoad isLoading={false} isError={isError} errorMessage={error?.message}>
      <PropsInjector props={props}>{children}</PropsInjector>
    </DataLoad>
  )
}

interface ProjectLoRaWanSensorsProps {
  projectId: number
}

export const ProjectLoRaWanSensors: FC<ProjectLoRaWanSensorsProps> = ({ projectId }) => (
  <ProjectLoRaWanSensorsLoader projectId={projectId}>
    <ProjectLoRaWanSensorsList />
  </ProjectLoRaWanSensorsLoader>
)
